// src/pages/PricingFlow.js
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { Container, Typography, CircularProgress, Box, Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import { PricingProvider, usePricing } from "../../contexts/PricingContext";
import StepperEtapa1 from "./StepperEtapa1";
import StepperEtapa2 from "./StepperEtapa2";
import ResumoDaPrecificacao from "./PricingResume";
import { getPricingById,countPricings } from "../../services/pricingService"; 
import { getClientById } from "../../services/clientService"; 
import { useCompany } from "../../contexts/CompanyContext";
import { PLAN_LIMITS, PLAN_LABELS } from "../../constants/planos";
import { getCompanyData } from "../../services/companyService";
import PropTypes from "prop-types";

const HydratePricing = ({ data }) => {
  const { loadFormData } = usePricing();
  const hydratedRef = React.useRef(false);
  useEffect(() => {
    if (!data || hydratedRef.current) return;
    hydratedRef.current = true;
    loadFormData((prev) => ({ ...prev, ...data }));
  }, [data, loadFormData]);
  return null;
};


HydratePricing.propTypes = {
  data: PropTypes.object.isRequired
};

const toArray = (input) => {
  if (!input) return [];
  return Array.isArray(input) ? input.filter(Boolean) : Object.values(input);
};

const normalizeValores = (arr) =>
  arr.map((it) => ({
    ...it,
    valor:
      typeof it?.valor === "string"
        ? Number(it.valor.replace(",", "."))
        : it?.valor ?? 0,
  })
);

const PricingFlow = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { company } = useCompany();
  const companyId = company?.companyId || null;

  const plan = company?.plan || "Free";

  const mode = useMemo(() => {
    if (location.pathname.endsWith("/resume")) return "resume";
    if (location.pathname.endsWith("/edit")) return "edit";
    return "create";
  }, [location.pathname]);

  const initialStep = mode === "resume" ? 3 : 1;

  const [stepperIndex, setStepperIndex] = useState(initialStep);
  const [loading, setLoading] = useState(mode !== "create");
  const [error, setError] = useState("");
  const [loadedData, setLoadedData] = useState(null);

  const [limitDialogOpen, setLimitDialogOpen] = useState(false);
  const [limitInfo, setLimitInfo] = useState({ current: 0, limit: 0 });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (mode !== "create" || !companyId) return;

        const currentCount = company?.pricingCount || await countPricings(companyId);
        const limit = PLAN_LIMITS[plan];
        const isUnlimited = limit === "unlimited";

        if (!isUnlimited && typeof limit === "number" && currentCount >= limit) {
          if (!alive) return;
          setLimitInfo({ current: currentCount, limit });
          setLimitDialogOpen(true);
        } else {
          setLoading(false);
        }
      } catch (e) {
        console.error(e);
        if (alive) {
          setError("Falha ao verificar limite do plano.");
          setLoading(false);
        }
      }
    })();
    return () => { alive = false; };
  }, [mode, companyId, plan]);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (mode === "create") return; 
      try {
        if (!companyId || !id) return;
        setLoading(true);
        setError("");
        const data = await getPricingById(companyId, id);
        if (!alive) return;
        if (!data) setError("Precificação não encontrada.");

        if (data?.idCliente) {
          const clientData = await getClientById(companyId, data.idCliente);
          if (clientData) data.dadosCliente = clientData;
        }

        const companyData = await getCompanyData(companyId);
        if (companyData) {
          data.funcionarios     = toArray(companyData.employee);
          data.custosFixos      = normalizeValores(toArray(companyData.fixedCosts));
          data.custosVariaveis  = normalizeValores(toArray(companyData.variableCosts));
        }

        setLoadedData(data);
        setStepperIndex(initialStep);
      } catch (e) {
        console.error(e);
        if (alive) setError("Falha ao carregar a precificação.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [mode, id, companyId, initialStep]);

  const goToNextStepper = () => setStepperIndex(prev => prev + 1);
  const goToStepper1 = () => setStepperIndex(1);
  const goToStepper2 = () => setStepperIndex(2);

  return (
    <Container maxWidth="lg" sx={{ pt: 8, pb: 8 }}>
      <Typography variant="h3" fontWeight={700} align="center" gutterBottom>
        {mode === "create" && "Precificação — Nova"}
        {mode === "edit" && "Precificação — Editar"}
        {mode === "resume" && "Precificação — Resumo"}
      </Typography>

      {/* Dialog de limite atingido */}
      <Dialog open={limitDialogOpen} onClose={() => {}} fullWidth maxWidth="sm">
        <DialogTitle>Limite do plano atingido</DialogTitle>
        <DialogContent dividers>
          <Typography gutterBottom>
            Seu plano <strong>{PLAN_LABELS[plan] || plan}</strong> permite no máximo{" "}
            <strong>{limitInfo.limit}</strong> precificação{limitInfo.limit === 1 ? "" : "es"}.
          </Typography>
          <Typography gutterBottom>
            Você já possui <strong>{limitInfo.current}</strong>. Para criar novas precificações, exclua alguma existente
            ou faça o upgrade do plano.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => navigate("/pricing")} variant="outlined">
            Ir para listagem
          </Button>
          {/* <Button onClick={() => navigate("/billing")} variant="contained">
            Ver planos / Upgrade
          </Button> */}
        </DialogActions>
      </Dialog>

      <PricingProvider>
        {!!loadedData && <HydratePricing data={loadedData} />}

        {loading ? (
          <Box py={6} display="flex" justifyContent="center"><CircularProgress /></Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <>
            {stepperIndex === 1 && (
              <StepperEtapa1 onFinish={goToNextStepper} pricingId={id} mode={mode} />
            )}
            {stepperIndex === 2 && (
              <StepperEtapa2 onFinish={goToNextStepper} pricingId={id} mode={mode} />
            )}
            {stepperIndex === 3 && (
              <ResumoDaPrecificacao
                pricingId={id}
                mode={mode}
                onBackToEtapa1={goToStepper1}
                onBackToEtapa2={goToStepper2}
              />
            )}
          </>
        )}
      </PricingProvider>
    </Container>
  );
};

export default PricingFlow;
